"use client";

import * as React from "react";
import { Bar, BarChart, CartesianGrid, Cell, XAxis, YAxis } from "recharts";

import { withOccurrenceKeys } from "../../lib/occurrence-keys";
import {
	ChartContainer,
	ChartLegend,
	ChartLegendContent,
	ChartTooltip,
	ChartTooltipContent,
	seriesColor,
} from "./chart";
import {
	CHART_AXIS_PROPS,
	CHART_GRID_PROPS,
	type CartesianChartProps,
} from "./chart-cartesian";

export type ChartBarProps = CartesianChartProps & {
	/**
	 * - `default` — one bar per category for each series (grouped side by side
	 *   when there is more than one).
	 * - `stacked` — several series stacked into one bar per category.
	 * - `negative` — a single series where negative values take the colour of
	 *   `negativeKey` instead of the series colour.
	 * - `mixed` — a single series where each bar takes its own colour from
	 *   `config` keyed by the category value (pairs well with `layout="horizontal"`).
	 */
	variant?: "default" | "stacked" | "negative" | "mixed";
	/**
	 * Bar orientation as seen on screen.
	 * - `vertical` (default) — vertical columns, category on the X axis.
	 * - `horizontal` — horizontal bars, category on the Y axis.
	 */
	layout?: "vertical" | "horizontal";
	/**
	 * `config` key supplying the colour for negative values under
	 * `variant="negative"`. Defaults to the series colour.
	 */
	negativeKey?: string;
	/** Corner radius for the bars. Defaults to `4`. */
	radius?: number;
	/**
	 * Which corners of a stacked bar are rounded. Applies only to
	 * `variant="stacked"`.
	 * - `outer` (default) - only the outer end of each stack is rounded, so the
	 *   segments meet square.
	 * - `all` - every segment has all four corners rounded.
	 */
	stackedCorners?: "outer" | "all";
	/**
	 * Upper bound on bar thickness, in pixels. Unset, a bar takes its share of the
	 * category slot, so a chart with few categories draws heavy blocks; capping it
	 * keeps the bars slim and lets the gaps grow instead.
	 */
	maxBarSize?: number;
	/**
	 * Grows the bars in on mount. Turn off when the chart is rasterized right
	 * after it renders, which would otherwise capture it part-drawn. Unset, it
	 * falls through to Recharts' own `Bar` default (on in the browser).
	 */
	isAnimationActive?: boolean;
};

/**
 * Resolves the `config` key colouring a single bar, for the variants that colour
 * per datum rather than per series. `null` for the variants that don't.
 */
type CellKey =
	| ((datum: Record<string, unknown>, seriesKey: string) => string)
	| null;

/**
 * Bar chart built on the shared `ChartContainer` primitives. Palette-agnostic:
 * every colour is read from `config` as `var(--color-<key>)`.
 */
export function ChartBar({
	data,
	config,
	series,
	categoryKey,
	variant = "default",
	layout = "vertical",
	negativeKey,
	radius = 4,
	stackedCorners = "outer",
	maxBarSize,
	isAnimationActive,
	className,
	valueFormatter,
	categoryFormatter,
	hideTooltip = false,
	showLegend,
}: ChartBarProps) {
	const isHorizontal = layout === "horizontal";
	const perCategoryColour = variant === "mixed";

	// `mixed` and `negative` both colour each bar from its own datum rather than
	// from the series, so they share one <Cell> path; the variant only decides
	// which `config` key each datum maps to.
	const cellKey = ((): CellKey => {
		switch (variant) {
			case "mixed":
				return (datum) => String(datum[categoryKey]);
			case "negative":
				return (datum, seriesKey) =>
					Number(datum[seriesKey]) < 0 ? (negativeKey ?? seriesKey) : seriesKey;
			case "default":
			case "stacked":
				return null;
		}
	})();

	// Per-bar colouring describes a single measure, so extra series have nothing to
	// colour with.
	const drawnSeries = cellKey ? series.slice(0, 1) : series;
	const keyedData = withOccurrenceKeys(data, (datum) =>
		String(datum[categoryKey]),
	);

	// Recharts corner order: top-left, top-right, bottom-right, bottom-left.
	const outerEnd = isHorizontal
		? [0, radius, radius, 0]
		: [radius, radius, 0, 0];
	// Square joins assume a non-negative stack: its outer end belongs to the last
	// series drawn with a positive value. A datum with any negative value keeps
	// the scalar radius on every segment.
	const stackedRadius = (datum: Record<string, unknown>, key: string) => {
		if (series.some((k) => Number(datum[k]) < 0)) {
			return radius;
		}
		return series.findLast((k) => Number(datum[k]) > 0) === key ? outerEnd : 0;
	};
	const withLegend = showLegend ?? drawnSeries.length > 1;

	const categoryAxisProps = {
		dataKey: categoryKey,
		...CHART_AXIS_PROPS,
		tickFormatter: categoryFormatter,
		// Under `mixed` each bar is already named in the tooltip and legend.
		hide: perCategoryColour,
	};

	return (
		<ChartContainer config={config} className={className}>
			<BarChart
				accessibilityLayer
				data={data}
				// Recharts names its `layout` for the axis the categories sit on, the
				// inverse of the visual bar direction.
				layout={isHorizontal ? "vertical" : "horizontal"}
			>
				<CartesianGrid
					{...CHART_GRID_PROPS}
					horizontal={!isHorizontal}
					vertical={isHorizontal}
				/>
				{/* Not a fragment: Recharts 2 cannot see axes inside a React 19 fragment. */}
				{isHorizontal && <YAxis type="category" {...categoryAxisProps} />}
				{isHorizontal && <XAxis type="number" hide />}
				{!isHorizontal && <XAxis type="category" {...categoryAxisProps} />}
				{!hideTooltip && (
					<ChartTooltip
						content={
							<ChartTooltipContent
								valueFormatter={valueFormatter}
								nameKey={perCategoryColour ? categoryKey : undefined}
								hideLabel={perCategoryColour}
							/>
						}
					/>
				)}
				{withLegend && <ChartLegend content={<ChartLegendContent />} />}
				{drawnSeries.map((key) => (
					<Bar
						key={key}
						dataKey={key}
						stackId={variant === "stacked" ? "a" : undefined}
						radius={radius}
						maxBarSize={maxBarSize}
						isAnimationActive={isAnimationActive}
						fill={seriesColor(key)}
					>
						{cellKey &&
							keyedData.map(({ item: datum, key: datumKey }) => (
								<Cell key={datumKey} fill={seriesColor(cellKey(datum, key))} />
							))}
						{variant === "stacked" &&
							stackedCorners === "outer" &&
							keyedData.map(({ item: datum, key: datumKey }) => (
								<Cell
									key={datumKey}
									// Cell is typed as a plain SVG element, but Recharts hands its
									// radius to the bar's Rectangle, which takes per-corner arrays.
									radius={stackedRadius(datum, key) as unknown as number}
								/>
							))}
					</Bar>
				))}
			</BarChart>
		</ChartContainer>
	);
}
